import React, { useRef, useState, useEffect, useCallback } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

interface MobileTabScrollerProps {
  children: React.ReactNode;
  isMobile: boolean;
}

/**
 * On mobile, finds the .ant-tabs-nav element and injects
 * left/right scroll arrow buttons directly into it via a portal-like
 * approach using a sticky positioned overlay div.
 */
export const MobileTabScroller: React.FC<MobileTabScrollerProps> = ({ children, isMobile }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [navHeight, setNavHeight] = useState(60);

  const getNavWrap = useCallback((): HTMLElement | null => {
    if (!wrapperRef.current) return null;
    return wrapperRef.current.querySelector('.ant-tabs-nav-wrap') as HTMLElement | null;
  }, []);

  const getNav = useCallback((): HTMLElement | null => {
    if (!wrapperRef.current) return null;
    return wrapperRef.current.querySelector('.ant-tabs-nav') as HTMLElement | null;
  }, []);

  const updateScrollState = useCallback(() => {
    const wrap = getNavWrap();
    const nav = getNav();
    if (!wrap) return;
    setCanScrollLeft(wrap.scrollLeft > 4);
    setCanScrollRight(wrap.scrollLeft + wrap.clientWidth < wrap.scrollWidth - 4);
    if (nav) setNavHeight(nav.offsetHeight);
  }, [getNavWrap, getNav]);

  const scrollLeft = useCallback(() => {
    const wrap = getNavWrap();
    if (!wrap) return;
    wrap.scrollBy({ left: -150, behavior: 'smooth' });
    setTimeout(updateScrollState, 350);
  }, [getNavWrap, updateScrollState]);

  const scrollRight = useCallback(() => {
    const wrap = getNavWrap();
    if (!wrap) return;
    wrap.scrollBy({ left: 150, behavior: 'smooth' });
    setTimeout(updateScrollState, 350);
  }, [getNavWrap, updateScrollState]);

  useEffect(() => {
    if (!isMobile) return;

    let wrap: HTMLElement | null = null;

    const attach = () => {
      wrap = getNavWrap();
      if (!wrap) return false;
      wrap.addEventListener('scroll', updateScrollState, { passive: true });
      updateScrollState();
      return true;
    };

    if (!attach()) {
      const observer = new MutationObserver(() => {
        if (attach()) observer.disconnect();
      });
      if (wrapperRef.current) {
        observer.observe(wrapperRef.current, { childList: true, subtree: true });
      }
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateScrollState);
    return () => {
      wrap?.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [isMobile, getNavWrap, updateScrollState]);

  useEffect(() => {
    if (!isMobile) return;
    const timer = setTimeout(updateScrollState, 200);
    return () => clearTimeout(timer);
  }, [isMobile, updateScrollState, children]);

  if (!isMobile) return <>{children}</>;

  const arrowBtn: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    zIndex: 55,
    width: '36px',
    height: `${navHeight}px`,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    color: 'var(--brand-gold)',
    transition: 'opacity 0.2s',
    padding: 0,
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      {/* Left arrow — gradient fade + button */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          aria-label="Scroll tabs left"
          style={{
            ...arrowBtn,
            left: 0,
            background: 'linear-gradient(to right, var(--bg-card) 60%, transparent)',
          }}
        >
          <LeftOutlined />
        </button>
      )}

      {children}

      {/* Right arrow — gradient fade + button */}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          aria-label="Scroll tabs right"
          style={{
            ...arrowBtn,
            right: 0,
            background: 'linear-gradient(to left, var(--bg-card) 60%, transparent)',
          }}
        >
          <RightOutlined />
        </button>
      )}
    </div>
  );
};
